import { useState, useEffect } from 'react'
import FormModal from '../FormModal'

// Free-text, with suggestions — same treatment locations.location_type gets.
// The point of the generic design is that a new kind of handout needs no
// schema change, so this must not become a <select>.
const LABEL_SUGGESTIONS = ['Dream', 'Letter', 'Vision', 'Prophecy', 'Memory']

const formStyles = {
  field: {
    marginBottom: '0.4rem',
  },
  label: {
    display: 'block',
    color: 'var(--text-primary)',
    marginBottom: '0.4rem',
    fontSize: '0.95rem',
    fontWeight: 500,
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    background: 'var(--accent-deep)',
    border: '1px solid var(--accent-teal)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    background: 'var(--accent-deep)',
    border: '1px solid var(--accent-teal)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    minHeight: 'clamp(140px, 22vh, 380px)',
    resize: 'vertical',
  },
  smallTextarea: {
    width: '100%',
    padding: '0.75rem',
    background: 'var(--accent-deep)',
    border: '1px solid var(--accent-teal)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    minHeight: 'clamp(100px, 16vh, 260px)',
    resize: 'vertical',
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  checkboxInput: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  },
}

// entry: the journal entry being edited, or null when creating a new one.
// onSubmit receives the full PUT/POST-shaped payload, including is_visible
// (see the note below — it must always be sent, never omitted).
function JournalEntryForm({ isOpen, onClose, entry = null, onSubmit }) {
  const [formData, setFormData] = useState({
    title: '',
    label: '',
    body: '',
    dm_notes: '',
    is_visible: 0,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (entry) {
      setFormData({
        title: entry.title || '',
        label: entry.label || '',
        body: entry.body || '',
        dm_notes: entry.dm_notes || '',
        is_visible: entry.is_visible || 0,
      })
    } else {
      setFormData({
        title: '',
        label: '',
        body: '',
        dm_notes: '',
        is_visible: 0,
      })
    }
  }, [entry])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // is_visible is always included, even when 0 — PUT is a full replace
      // and the handler defaults it to 0 when the field is absent, so
      // leaving it out here would silently re-hide a revealed entry.
      await onSubmit(formData)
      onClose()
    } catch (error) {
      console.error('Form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={entry ? 'Edit Journal Entry' : 'New Journal Entry'}
      submitText={entry ? 'Save changes' : 'Create entry'}
      isSubmitting={isSubmitting}
    >
      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="journal-title">Title *</label>
        <input
          type="text"
          id="journal-title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          required
          style={formStyles.input}
          placeholder="e.g., The Cracking Cage"
        />
      </div>

      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="journal-label">Kind</label>
        <input
          type="text"
          id="journal-label"
          name="label"
          list="journal-label-suggestions"
          value={formData.label}
          onChange={handleChange}
          style={formStyles.input}
          placeholder="Dream"
        />
        <datalist id="journal-label-suggestions">
          {LABEL_SUGGESTIONS.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      </div>

      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="journal-body">What the player reads</label>
        <textarea
          id="journal-body"
          name="body"
          value={formData.body}
          onChange={handleChange}
          style={formStyles.textarea}
          placeholder="You are not asleep so much as risen…"
        />
        <small style={{ color: 'var(--text-muted)' }}>
          Markdown, and [[wiki-links]] resolve to entity previews.
        </small>
      </div>

      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="journal-dm-notes">DM notes (never sent to the player)</label>
        <textarea
          id="journal-dm-notes"
          name="dm_notes"
          value={formData.dm_notes}
          onChange={handleChange}
          style={formStyles.smallTextarea}
          placeholder="What it foreshadows, how to run it, threads and payoffs…"
        />
      </div>

      <div style={formStyles.field}>
        <label style={formStyles.checkbox}>
          <input
            type="checkbox"
            name="is_visible"
            checked={formData.is_visible === 1}
            onChange={handleChange}
            style={formStyles.checkboxInput}
          />
          <span style={{ color: 'var(--text-primary)' }}>Revealed to the player</span>
        </label>
      </div>
    </FormModal>
  )
}

export default JournalEntryForm
